import { Typography } from "antd";
import moment from "moment";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Socket } from "socket.io-client";
import { useGetOneTransaction } from "../../../api/currency";
import { CustomButton } from "../../../components/button/buttonCustom";
import PaymentStatus from "../../../types/paymentStatus.type";
import useResponsive from "../../../utils/hooks/useResponsive";
import { formatNumber } from "../../../utils/list.utils";
import StatusForm from "../../history/component/form/status-form";
import TextContent from "../../history/component/form/text-content";
import { Fiat } from "../../request-money/component/form-transfer-props";

interface FormWithdrawSuccessProps {
  responseWithdrawal: any;
  socket: Socket<any, any>;
  setPrintableFormUrl: React.Dispatch<React.SetStateAction<string | null>>;
}

const FormWithdrawSuccess = ({
  responseWithdrawal,
  socket,
  setPrintableFormUrl,
}: FormWithdrawSuccessProps) => {
  const { isTablet } = useResponsive();

  const navigate = useNavigate();

  const handleDetail = () => {
    const isFiat = Fiat.includes(responseWithdrawal?.currency);
    navigate(
      `/history?tab=${isFiat ? 'fiat' : 'crypto'}&orderId=${responseWithdrawal.id}&type=crypto-withdrawals`
    );
  };

  const { data = { data: { data: { status: "", reason: "", printableFormUrl: null } } }, refetch } =
    useGetOneTransaction(responseWithdrawal?.id);

  useEffect(() => {
    if (!responseWithdrawal) return;
    const handleDetailWalletUpdated = () => {
      refetch();
    };
    socket.on(
      `user.walletOrder.${Number(responseWithdrawal?.id || 0)}.updated`,
      handleDetailWalletUpdated
    );

    return () => {
      socket.off(
        `user.userWallet.${Number(responseWithdrawal?.id || 0)}.updated`,
        handleDetailWalletUpdated
      );
    };
  }, [socket, responseWithdrawal, refetch]);

  useEffect(() => {
    if (!data?.data?.data) return;
    setPrintableFormUrl(data?.data?.data?.printableFormUrl);
  }, [data, data?.data?.data]);

  const reasonStatus = () => {
    switch (data?.data?.data?.status) {
      case PaymentStatus.PENDING:
        return "To finalize your withdrawal, please wait while your request is being reviewed.";
      case PaymentStatus.REJECTED:
        return data?.data?.data?.reason || "N/A";
      case PaymentStatus.CANCELLED:
        return (
          data?.data?.data?.reason ||
          "You’ve canceled the withdrawal order. Funds remain in your account."
        );
      case PaymentStatus.FAILED:
        return data?.data?.data?.reason || "N/A";
      case PaymentStatus.COMPLETED:
        return (
          data?.data?.data?.reason ||
          "Withdrawal has been completed. Check your portfolio for details."
        );
      default:
        return "";
    }
  };

  return (
    <div>
      <div className="form-history fix-bottom">
        <StatusForm
          title={`Withdrawal ${responseWithdrawal?.orderId}`}
          status={data?.data?.data.status}
          content={reasonStatus()}
        />
        <div className="modal-balance">
          <div className="value-balance">
            <Typography style={{ fontSize: 16, color: "white" }}>
              Amount
            </Typography>
            <Typography.Text className="text-gradient">
              {formatNumber(
                Number(responseWithdrawal?.amount) || 0,
                responseWithdrawal?.decimals || 4
              )}{" "}
              <Typography.Text
                className="text-gradient"
                style={{
                  fontSize: isTablet ? "20px" : "24px",
                }}
              >
                {responseWithdrawal?.currency || ""}
              </Typography.Text>
            </Typography.Text>
          </div>
        </div>
        <div className="form-history-content">
          <TextContent
            label="Beneficiary Will Receive"
            value={`${(
              Number(responseWithdrawal?.amount) -
              Number(responseWithdrawal?.fee)
            ).toFixed(responseWithdrawal?.decimals || 4)} ${responseWithdrawal?.currency || ""
              }`}
            color="rgba(242, 138, 40, 1)"
          />
        </div>
        <div className="form-history-content border-top" style={{ gap: 24 }}>
          <TextContent
            label="Currency"
            value={responseWithdrawal?.currency || ""}
          />
          <TextContent
            label="Chain Type"
            value={responseWithdrawal?.chainType}
          />
          <TextContent
            label={responseWithdrawal?.address ? "Address" : "Beneficiary"}
            value={
              responseWithdrawal?.address ||
              responseWithdrawal?.beneficiaryFullName
            }
            canCopy={!!responseWithdrawal?.address}
          />
          <TextContent
            label="Fees"
            value={`${formatNumber(
              responseWithdrawal?.fee || 0,
              responseWithdrawal?.decimals || 4
            )} ${responseWithdrawal?.currency || ""}`}
          />
        </div>
        <div className="form-history-content border-top">
          <TextContent
            label="Time"
            value={moment(responseWithdrawal?.createdAt).format(
              "HH:mm:ss DD/MM/YYYY"
            )}
          />
        </div>
      </div>
      <div className="form-withdraw-button">
        <CustomButton
          size={isTablet ? "medium" : "large"}
          text={"Details"}
          width={"100%"}
          onClick={handleDetail}
        />
      </div>
    </div>
  );
};

export default FormWithdrawSuccess;
